import React, { useState, useCallback } from 'react';
import type { Dependency, DependencyType } from '../../../types/cascadeDelete';
import { getDependencyTypePluralLabel } from '../../../types/cascadeDelete';

export interface DependencyTreeViewProps {
  /** Array of dependencies to display */
  dependencies: Dependency[];
  /** Whether groups are initially expanded */
  defaultExpanded?: boolean;
  /** Maximum items to show before collapsing */
  maxVisibleItems?: number;
  /** Additional CSS class names */
  className?: string;
}

/** Group dependencies by type for hierarchical display */
interface DependencyGroup {
  type: DependencyType;
  items: Dependency[];
  isExpanded: boolean;
}

/**
 * Connection icon SVG component
 */
const ConnectionIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`w-4 h-4 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10 3H6a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h4M16 17l5-5-5-5M19.8 12H9" />
  </svg>
);

/**
 * Task icon SVG component
 */
const TaskIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`w-4 h-4 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

/**
 * Chevron icon for expand/collapse
 */
const ChevronIcon: React.FC<{ isExpanded: boolean; className?: string }> = ({
  isExpanded,
  className = '',
}) => (
  <svg
    className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''} ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/**
 * Get the icon component for a dependency type
 */
const getTypeIcon = (type: DependencyType): React.FC<{ className?: string }> => {
  switch (type) {
    case 'connection':
      return ConnectionIcon;
    case 'task':
      return TaskIcon;
    default:
      return TaskIcon;
  }
};

/**
 * Get color classes for a dependency type
 */
const getTypeColorClasses = (type: DependencyType): { icon: string; badge: string } => {
  switch (type) {
    case 'connection':
      return {
        icon: 'text-primary-500 dark:text-primary-400',
        badge: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
      };
    case 'task':
      return {
        icon: 'text-warning-500 dark:text-warning-400',
        badge: 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300',
      };
    default:
      return {
        icon: 'text-neutral-500 dark:text-neutral-400',
        badge: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
      };
  }
};

/**
 * DependencyTreeView Component
 *
 * Displays dependencies in a hierarchical tree view grouped by type.
 * Supports expandable/collapsible groups and shows dependency details.
 */
export const DependencyTreeView: React.FC<DependencyTreeViewProps> = ({
  dependencies,
  defaultExpanded = true,
  maxVisibleItems = 10,
  className = '',
}) => {
  // Group dependencies by type
  const groupedDeps = dependencies.reduce<Record<DependencyType, Dependency[]>>(
    (acc, dep) => {
      if (!acc[dep.type]) {
        acc[dep.type] = [];
      }
      acc[dep.type].push(dep);
      return acc;
    },
    {} as Record<DependencyType, Dependency[]>
  );

  // Create initial groups state
  const initialGroups: DependencyGroup[] = (
    Object.entries(groupedDeps) as [DependencyType, Dependency[]][]
  ).map(([type, items]) => ({
    type,
    items,
    isExpanded: defaultExpanded,
  }));

  const [groups, setGroups] = useState<DependencyGroup[]>(initialGroups);
  const [showAllItems, setShowAllItems] = useState<Record<DependencyType, boolean>>({} as Record<DependencyType, boolean>);

  // Toggle group expansion
  const toggleGroup = useCallback((type: DependencyType) => {
    setGroups((prev) =>
      prev.map((group) =>
        group.type === type ? { ...group, isExpanded: !group.isExpanded } : group
      )
    );
  }, []);

  // Toggle show all items for a group
  const toggleShowAll = useCallback((type: DependencyType) => {
    setShowAllItems((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  }, []);

  if (dependencies.length === 0) {
    return (
      <div className={`text-sm text-neutral-500 dark:text-neutral-400 italic ${className}`}>
        No dependencies found
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {groups.map((group) => {
        const IconComponent = getTypeIcon(group.type);
        const colorClasses = getTypeColorClasses(group.type);
        const showAll = showAllItems[group.type] ?? false;
        const visibleItems = showAll ? group.items : group.items.slice(0, maxVisibleItems);
        const hasMore = group.items.length > maxVisibleItems;

        return (
          <div
            key={group.type}
            className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden"
          >
            {/* Group Header */}
            <button
              type="button"
              onClick={() => toggleGroup(group.type)}
              className={[
                'w-full flex items-center gap-3 px-4 py-3',
                'bg-neutral-50 dark:bg-neutral-800/50',
                'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                'transition-colors duration-150',
                'text-left',
              ].join(' ')}
              aria-expanded={group.isExpanded}
              aria-controls={`dependency-group-${group.type}`}
            >
              <ChevronIcon
                isExpanded={group.isExpanded}
                className="text-neutral-400 dark:text-neutral-500 flex-shrink-0"
              />
              <IconComponent className={`${colorClasses.icon} flex-shrink-0`} />
              <span className="flex-1 font-medium text-sm text-neutral-900 dark:text-white">
                {getDependencyTypePluralLabel(group.type)}
              </span>
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorClasses.badge}`}
              >
                {group.items.length}
              </span>
            </button>

            {/* Group Content */}
            {group.isExpanded && (
              <div
                id={`dependency-group-${group.type}`}
                className="border-t border-neutral-200 dark:border-neutral-700"
              >
                <ul className="divide-y divide-neutral-100 dark:divide-neutral-700/50">
                  {visibleItems.map((item) => (
                    <li
                      key={item.id}
                      className="px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-neutral-50 dark:hover:bg-neutral-800/30 transition-colors"
                    >
                      <span className="text-sm text-neutral-700 dark:text-neutral-300 truncate">
                        {item.name}
                      </span>
                      <span className="text-xs text-neutral-400 dark:text-neutral-500 font-mono flex-shrink-0">
                        {item.id.slice(0, 8)}...
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Show more/less toggle */}
                {hasMore && (
                  <div className="px-4 py-2 border-t border-neutral-100 dark:border-neutral-700/50">
                    <button
                      type="button"
                      onClick={() => toggleShowAll(group.type)}
                      className={[
                        'text-xs font-medium',
                        'text-primary-600 hover:text-primary-700',
                        'dark:text-primary-400 dark:hover:text-primary-300',
                        'transition-colors duration-150',
                      ].join(' ')}
                    >
                      {showAll
                        ? 'Show less'
                        : `Show ${group.items.length - maxVisibleItems} more`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

DependencyTreeView.displayName = 'DependencyTreeView';

export default DependencyTreeView;
